import React from 'react';
import styled from 'styled-components';
import { SectionContainer, SectionTitle } from '../../styles/components';
import aboutImage from '../../assets/about.jpg';

const AboutWrapper = styled.div`
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 0 var(--spacing-md);
`;

const AboutContent = styled.div`
  display: flex;
  gap: var(--spacing-xl);
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const AboutImage = styled.div`
  flex: 1;
  position: relative;
  
  img {
    width: 100%;
    height: auto;
    display: block;
    border: 4px solid var(--secondary);
  }
  
  &:after {
    content: '';
    position: absolute;
    top: var(--spacing-md);
    right: var(--spacing-md);
    width: 100%;
    height: 100%;
    border: 1px solid var(--accent-2);
    z-index: -1;
  }
  
  @media (max-width: 768px) {
    margin-bottom: var(--spacing-lg);
  }
`;

const AboutText = styled.div`
  flex: 1;
`;

const AboutParagraph = styled.p`
  margin-bottom: var(--spacing-md);
  font-size: 1.125rem;
  line-height: 1.6;
`;

const ExpertiseList = styled.ul`
  margin: var(--spacing-md) 0;
  
  li {
    margin-bottom: var(--spacing-sm);
    position: relative;
    padding-left: var(--spacing-lg);
    
    &:before {
      content: '✓';
      position: absolute;
      left: 0;
      color: var(--accent-2);
      font-weight: bold;
    }
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-md);
  margin-top: var(--spacing-xl);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatBox = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  padding: var(--spacing-lg);
  text-align: center;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: var(--accent-2);
  margin-bottom: var(--spacing-xs);
  font-family: 'Barlow Condensed', 'Helvetica Neue', 'Arial', sans-serif;
`;

const StatTitle = styled.div`
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const About: React.FC = () => {
  return (
    <SectionContainer id="about">
      <AboutWrapper>
        <SectionTitle>About Us</SectionTitle>

        <AboutContent>
          <AboutImage>
            <img
              src={aboutImage}
              alt="GS Autohaus Technicians"
            />
          </AboutImage>

          <AboutText>
            <AboutParagraph>
              GS Autohaus is San Francisco's premier auto service specialist for European and
              Japanese vehicles. Founded with a passion for precision engineering and
              exceptional service, we bring factory-trained expertise to every vehicle we service.
            </AboutParagraph>

            <AboutParagraph>
              What sets us apart is our specialized knowledge. Our team of certified
              technicians possesses the training, experience, and equipment to handle
              the most complex engineering challenges across European and Japanese makes.
            </AboutParagraph>

            <ExpertiseList>
              <li>Factory-trained and certified technicians with decades of combined experience</li>
              <li>BMW specialists with extensive knowledge of both classic and modern models</li>
              <li>Comprehensive expertise across all European luxury brands</li>
              <li>Japanese vehicle specialists focusing on performance and precision</li>
              <li>State-of-the-art diagnostic equipment and programming capabilities</li>
            </ExpertiseList>

            <AboutParagraph>
              We take pride in delivering dealership-quality service with personalized
              attention and transparent communication. Every repair and maintenance service
              is performed with meticulous care, ensuring your vehicle performs at its best.
            </AboutParagraph>
          </AboutText>
        </AboutContent>

        <StatsContainer>
          <StatBox>
            <StatNumber>20+</StatNumber>
            <StatTitle>Years Experience</StatTitle>
          </StatBox>
          <StatBox>
            <StatNumber>100%</StatNumber>
            <StatTitle>Customer Satisfaction Guarantee</StatTitle>
          </StatBox>
          <StatBox>
            <StatNumber>100%</StatNumber>
            <StatTitle>Quality Commitment</StatTitle>
          </StatBox>
        </StatsContainer>
      </AboutWrapper>
    </SectionContainer>
  );
};

export default About; 